<template>
  <eden-master-search
    separator-text="Resultado de Informes"
    :page-config="pageConfig"
    :page-actions-config="headerActions"
    :create-report-visible="createReportVisible"
    :academic-years="options.academicYears"
    :pagination-page-size="pageSize"
    :selected-academic-year="filter.academicYear"
    :report-available-academic-years="options.reportAvailableAcademicYears"
    :data="resultData"
    :row-actions="rowActions"
    @on-create-report="onOpenCreateReportHandler"
    @on-cancel-report="onCancelCreateReportHandler"
    @on-continue-create-report="onContinueCreateReportHandler"
    @on-search-result="getSearchResult(1)"
    @on-change-page="getSearchResult($event.pageIndex)"
    @on-change-academic-year="onChangeAcademicYearHandler"
    @on-save-submission="onSaveSubmission"
  />
</template>

<script>
import storeMixin from 'utils/mixins/storeGenerator';
import EdenMasterSearch from '../shared/components/EdenMasterSearch.vue';
import Service from '@/services/api/edenStudentVisualImpairments';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

const STORE_MODULE_NAME = 'studentVisualImpairmentsModule';
import edenStatus from '@/utils/constants/edenStatus.js';
import reportServices from '@/modules/report/api';
import documentServices from '@//modules/report/document';

export default {
  name: 'StudentVisualImpairments',
  components: {
    EdenMasterSearch,
  },
  mixins: [storeMixin, alertMixin],
  data: () => ({
    moduleName: STORE_MODULE_NAME,
    filter: {
      academicYear: null,
    },
    options: {
      academicYears: [],
      reportAvailableAcademicYears: [],
    },
    resultData: {
      result: [],
      pagination: {},
    },

    createReportVisible: null,
  }),
  computed: {
    pageSize() {
      return 10;
    },
    pageConfig() {
      return {
        title: 'Registro de Estudiantes con Impedimentos Visuales',
        icon: 'fas fa-file-chart-line',
        routes: [
          'Inicio',
          '618 Data',
          'Registro Estudiantes con Impedimentos Visuales',
        ],
      };
    },
    headerActions() {
      return [
        {
          text: 'Crear Reporte',
          icon: 'fas fa-file-plus pr-2',
          variant: 'success',
          visible: !this.createReportVisible,
          click: () => this.onOpenCreateReportHandler(),
        },
      ];
    },
    rowActions() {
      return [
        {
          icon: 'fas fa-redo text-success',
          text: 'Actualizar',
          visible: ({ statusId }) => statusId === edenStatus.NEW,
          click: this.onUpdateReport,
        },
        {
          icon: 'fas fa-flag text-success',
          text: 'Someter',
          visible: ({ statusId, canSubmitVisualImpairments }) =>
            statusId !== edenStatus.SUBMITTED && canSubmitVisualImpairments,
          click: this.enableSubmitReport,
        },
        {
          icon: 'fas fa-circle-play text-success',
          text: 'Generar',
          click: this.onGenerateHandler,
        },
      ];
    },
  },
  async mounted() {
    await this.getInit();
    await this.getSearchResult();
  },
  methods: {
    async getInit() {
      await this.getAvailableAcademicYears();
    },
    async getAvailableAcademicYears() {
      this.options.academicYears = (
        await Service.getAvailableAcademicYears()
      ).data;
    },
    async onOpenCreateReportHandler() {
      this.createReportVisible = true;
      this.options.reportAvailableAcademicYears =
        (await Service.getReportAvailableAcademicYears()).data || [];
      await Service.questionAnswerValidate(this.academicYear);
    },
    async onContinueCreateReportHandler(academicYearId) {
      if (academicYearId) {
        await Service.createEstudentVisualImpairmentsReport(academicYearId)
          .then(async ({ data }) => {
            if (data) {
              this.ShowToast(
                'Proceso Exitoso',
                'El reporte ha sido creado exitosamente.',
                'success'
              );
              this.onCancelCreateReportHandler();
              await this.getSearchResult();
              await this.getInit();
            }
          })
          .catch((err) => {
            this.ShowToast('', err.response?.data?.message, 'error');
          });
      }
    },
    async getSearchResult(pageIndex = 1) {
      const payload = {
        AcademicYearId: this.filter.academicYear?.id,
        PageIndex: pageIndex,
        PageSize: this.pageSize,
      };
      const { data: response } = await Service.getEdenStudentVisualImpairments(
        payload
      );
      const rows = response?.data
        ? response.data.map((x) => {
            return {
              ...x,
              enableSubmission: false,
              canSubmitVisualImpairments: x.percentage === 100,
              showSubmitErrorMessage: x.percentage !== 100
            };
          })
        : [];
      this.resultData = {
        result: rows,
        pagination: {
          ...this.resultData.pagination,
          currentPage: response.currentPage,
          totalItemCount: response.totalItemCount,
          pageCount: response.pageCount,
          count: response.count,
          perPage: this.pageSize,
        },
      };
    },
    onCancelCreateReportHandler() {
      this.createReportVisible = false;
    },
    onChangeAcademicYearHandler(value) {
      this.filter.academicYear = value;
    },
    enableSubmitReport(row) {
      row.enableSubmission = true;
      this.$forceUpdate();
    },
    async onSaveSubmission({ id, date }) {
      try {
        await Service.submitReport({ edenMasterId: id, date });
        this.ShowToast(
          '',
          'El reporte ha sido sometido exitosamente',
          'success'
        );
        await this.getSearchResult();
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ?? error?.message,
          'error'
        );
      }
    },
    async onUpdateReport({ id }) {
      try {
        await Service.updateReport(id);
        this.ShowToast(
          '',
          'El reporte ha sido actualizado exitosamente',
          'success'
        );
        await this.getSearchResult();
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ?? error?.message,
          'error'
        );
      }
    },
    async onGenerateHandler({ id }) {
      await reportServices
        .getReport(`studentvisualimpairments/${id}`)
        .then((res) => documentServices.Download(res.data));
    },
  },
};
</script>

<style></style>
